import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import {
  type GroupBy,
  SNAPSHOT_VIEWS,
  type SnapshotView,
} from 'lumen/utils/constants/market-snapshots-constants';

interface ViewOption {
  label: string;
  route: (typeof SNAPSHOT_VIEWS)[number]['route'];
  value: SnapshotView;
}

export interface MarketSnapshotsViewSwitcherSignature {
  Args: {
    fromDate: string | undefined;
    groupBy: GroupBy;
    toDate: string | undefined;
    view: SnapshotView;
  };
}

/** Switches between snapshot views (routes); not the group-by toggle. */
export default class MarketSnapshotsViewSwitcher extends Component<MarketSnapshotsViewSwitcherSignature> {
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): ViewOption[] {
    return SNAPSHOT_VIEWS.map(({ id, label, route }) => ({
      label: this.intl.t(label),
      route,
      value: id,
    }));
  }

  get selectedOption(): ViewOption | undefined {
    return this.options.find((option) => option.value === this.args.view);
  }

  @action
  onChange(option: ViewOption): void {
    // Columns differ per view, so only the date range and grouping carry over;
    // each view keeps its own page QP, so start the target on the first page.
    void this.router.transitionTo(option.route, {
      queryParams: {
        fromDate: this.args.fromDate,
        groupBy: this.args.groupBy,
        page: 1,
        toDate: this.args.toDate,
      },
    });
  }

  <template>
    <PrismSelectInput
      @onChange={{this.onChange}}
      @options={{this.options}}
      @value={{this.selectedOption}}
      data-test-id="snapshots-view-switcher"
    />
  </template>
}
